import { authFetch } from './api';

/**
 * An axios-shaped client over the platform's `authFetch`.
 *
 * WHY THIS EXISTS
 * ---------------
 * Three of the source's service modules — `aiRunsApi`, `aiRepositoryApi`, `reportsApi`,
 * ~600 lines between them — open with the same 25-line preamble: `axios.create()` with a
 * base URL, a request interceptor that attaches a bearer token from `localStorage`, and a
 * response interceptor that redirects to `/login` on 401. Every method below that preamble
 * is written against the resulting `api` object: `api.get(...)`, `api.post(...)`, and reads
 * of `response.data` / `response.headers`.
 *
 * The platform authenticates with a session cookie, not a bearer token, so the preamble
 * cannot be ported as-is. But the preamble is the ONLY part that cannot. By reproducing
 * axios's shape here, each service file replaces its preamble with a one-line import and
 * every method body ports verbatim — no rewriting of ~600 lines around a different
 * transport, and no drift to review later.
 *
 * This is the same seam already taken for `aiKnowledgeApi` and for the licensing methods
 * in `api.ts`; §1.2 lists data access as an allowed deviation. The difference is that
 * those were rewritten call-by-call, and this preserves the call sites exactly.
 *
 * WHAT IS REPRODUCED, AND WHAT IS NOT
 * -----------------------------------
 * Only the surface those three files actually use: the five verbs, `responseType: 'blob'`,
 * `timeout`, a `params` object, and `{ data, headers }` on the way back. It is not a
 * general axios replacement and should not grow into one — if a future port needs more of
 * axios than this, add the real dependency rather than deepening the imitation.
 *
 * Two behaviours are deliberately NOT reproduced:
 *
 *   - The bearer-token request interceptor. `authFetch` sends the session cookie; there is
 *     no token to attach.
 *   - The 401 redirect. The platform shell already handles an expired session centrally,
 *     and a second redirect would race it. (Same call made for `aiKnowledgeApi`.)
 *
 * Errors are shaped like axios's: rejection carries `.response.status` and `.response.data`,
 * because the screens' catch blocks read `err.response?.data?.error`.
 */

/** The subset of axios's per-request config these services pass. */
interface RequestConfig {
  params?: Record<string, unknown>;
  responseType?: 'blob';
  timeout?: number;
  headers?: Record<string, string>;
}

/** Axios's response shape, narrowed to the fields the call sites read. */
interface AxiosLikeResponse<T = any> {
  data: T;
  headers: Record<string, string>;
  status: number;
}

/** Axios's error shape: `err.response?.data?.error` is what the screens check. */
export class AprApiError extends Error {
  response: { status: number; data: any };

  constructor(status: number, data: any) {
    super(data?.error || data?.message || `HTTP ${status}`);
    this.name = 'AprApiError';
    this.response = { status, data };
  }
}

const BASE = '/api/apr';

function buildUrl(url: string, params?: Record<string, unknown>): string {
  const full = url.startsWith('http') ? url : `${BASE}${url}`;
  if (!params) return full;
  const qs = new URLSearchParams(
    Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null)
      .map(([k, v]) => [k, String(v)]),
  ).toString();
  return qs ? `${full}${full.includes('?') ? '&' : '?'}${qs}` : full;
}

/** Axios exposes response headers lower-cased and subscriptable; mirror that. */
function toHeaderBag(res: Response): Record<string, string> {
  const bag: Record<string, string> = {};
  res.headers.forEach((value, key) => {
    bag[key.toLowerCase()] = value;
  });
  return bag;
}

async function request<T = any>(
  method: string,
  url: string,
  body?: unknown,
  config: RequestConfig = {},
): Promise<AxiosLikeResponse<T>> {
  const { params, responseType, timeout, headers } = config;

  // `timeout` is one of the config keys the source passes (a 5-minute ceiling on the
  // long-running analysis start). fetch has no timeout option, so drive it from a signal.
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer =
    timeout && controller ? setTimeout(() => controller.abort(), timeout) : undefined;

  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

  try {
    const res = await authFetch(buildUrl(url, params), {
      method,
      ...(controller ? { signal: controller.signal } : {}),
      ...(headers ? { headers } : {}),
      ...(body === undefined
        ? {}
        : { body: isFormData ? (body as FormData) : JSON.stringify(body) }),
    });

    if (!res.ok) {
      // Axios rejects on non-2xx and puts the parsed body on `error.response.data`.
      const data = await res.json().catch(() => ({}));
      throw new AprApiError(res.status, data);
    }

    const data =
      responseType === 'blob'
        ? await res.blob()
        : await res.json().catch(() => null); // 204s and empty bodies

    return { data: data as T, headers: toHeaderBag(res), status: res.status };
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

const api = {
  get: <T = any>(url: string, config?: RequestConfig) => request<T>('GET', url, undefined, config),
  delete: <T = any>(url: string, config?: RequestConfig) =>
    request<T>('DELETE', url, undefined, config),
  post: <T = any>(url: string, body?: unknown, config?: RequestConfig) =>
    request<T>('POST', url, body, config),
  put: <T = any>(url: string, body?: unknown, config?: RequestConfig) =>
    request<T>('PUT', url, body, config),
  patch: <T = any>(url: string, body?: unknown, config?: RequestConfig) =>
    request<T>('PATCH', url, body, config),
};

export default api;
